import { IntlShape } from 'react-intl';
import { getPaymentMethods } from '../commands/walletCommands';
import {
  blackListedPaymentMethodsForFirstDeposit,
  MAX_QUICK_METHODS_DISPLAYED_IF_FULL,
  MAX_QUICK_METHODS_DISPLAYED_IF_NOT_FULL,
  NUVEI_SOURCES,
  OTHER_PAYMENT_METHODS_ID,
} from '../data-types/wallet/walletConstants';
import { PAYMENT_METHODS_TRANSACTION_TYPE_ID, PAYMENT_PROVIDER } from '../data-types/wallet/walletEnums';
import { PaymentMethodWithNoAccounts, SanitizedPaymentMethod } from '../data-types/wallet/walletInterfaces';
import { setErrorDialog } from '../state/actions/dialogActions';
import { setUserPaymentMethodsData, setUserPaymentMethodsLoadingFlag } from '../state/actions/walletActions';
import { store } from '../state/store';

export const getPaymentMethodsThatCanBeRendered = (cards: SanitizedPaymentMethod[], canAddNew: boolean) => {
  if (cards.length > MAX_QUICK_METHODS_DISPLAYED_IF_NOT_FULL && canAddNew) {
    return cards.slice(0, MAX_QUICK_METHODS_DISPLAYED_IF_NOT_FULL);
  }

  if (cards.length > MAX_QUICK_METHODS_DISPLAYED_IF_FULL && !canAddNew) {
    return cards.slice(0, MAX_QUICK_METHODS_DISPLAYED_IF_FULL);
  }

  return cards;
};

export const loadPaymentMethods = async (
  showErrorMessage: boolean,
  transactionTypeId: PAYMENT_METHODS_TRANSACTION_TYPE_ID,
  includeInactive?: boolean,
) => {
  try {
    setUserPaymentMethodsLoadingFlag(true);
    const { cards, canAddNew, paymentMethodsWithNoAccounts } = await getPaymentMethods(transactionTypeId, includeInactive);
    setUserPaymentMethodsData({ canAddNew, paymentMethodsWithNoAccounts, cards, loadingPaymentMethods: false });
  } catch (e) {
    setUserPaymentMethodsLoadingFlag(false);
    if (showErrorMessage) {
      setErrorDialog(e.message);
    }
  }
};

export const usesDynamicPaymentProvider = (paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[]) => {
  const atLeastOneMethodIsNotSafecharge =
    paymentMethodsWithNoAccounts.length > 0 &&
    paymentMethodsWithNoAccounts.reduce((result, item) => result || item.sourceGatewayName !== PAYMENT_PROVIDER.NUVEI, false);
  return atLeastOneMethodIsNotSafecharge;
};

export const hasOnlyNuveiPaymentProvider = (paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[]) =>
  paymentMethodsWithNoAccounts.length > 0 &&
  paymentMethodsWithNoAccounts.reduce((result, item) => result && item.sourceGatewayName === PAYMENT_PROVIDER.NUVEI, true);

export const isUsingCpayPaymentProvider = (
  paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[],
  selectedMethod: PaymentMethodWithNoAccounts | undefined,
) => {
  return (
    (paymentMethodsWithNoAccounts.length === 1 && paymentMethodsWithNoAccounts[0].sourceGatewayName === PAYMENT_PROVIDER.CPAY) ||
    (selectedMethod && selectedMethod.sourceGatewayName === PAYMENT_PROVIDER.CPAY)
  );
};

export const isPaymentIdentifierBlank = (identifier: string) => [' ', ''].includes(identifier);

const methodsMatch = (methodWithAccount: SanitizedPaymentMethod, methodWithNoAccount: PaymentMethodWithNoAccounts) => {
  const methodIdMatches = methodWithNoAccount.id === methodWithAccount.id;
  const methodNoAccountSource = Number(methodWithNoAccount.source);
  const methodWithAccountSource = Number(methodWithAccount.source);
  const areBothFromNuvei = NUVEI_SOURCES.includes(methodNoAccountSource) && NUVEI_SOURCES.includes(methodWithAccountSource);
  const providersAreIdentical = methodNoAccountSource === methodWithAccountSource;
  const providerMatches = providersAreIdentical || areBothFromNuvei;

  return methodIdMatches && providerMatches;
};

export const filterOutDisabledSavedPaymentMethodsAndSort = (
  cards: SanitizedPaymentMethod[],
  paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[],
) => {
  let orderedCards: SanitizedPaymentMethod[] = [];

  paymentMethodsWithNoAccounts.forEach(methodWithNoAccount => {
    const savedMethodsForIdAndSource = cards.filter(card => methodsMatch(card, methodWithNoAccount));

    orderedCards = [...orderedCards, ...savedMethodsForIdAndSource];
  });

  return orderedCards;
};

export const getOtherPaymentMethodOption = (intl: IntlShape, displayAddNew: boolean): SanitizedPaymentMethod => ({
  id: OTHER_PAYMENT_METHODS_ID,
  identifierShort: intl.formatMessage({
    id: displayAddNew ? 'profile.wallet.add_payment_method_short' : 'common.view_more',
  }),
  identifier: intl.formatMessage({
    id: displayAddNew ? 'profile.wallet.add_payment_method' : 'common.view_more',
  }),
  color: '',
  expiry: '',
  logo: '',
  reference: '',
  source: -1,
  name: '',
  sourceReferenceId: '',
  requiredAccountId: null,
  isActive: true,
});

export const getSingleDynamicPaymentMethod = (
  shortlistedCards: SanitizedPaymentMethod[],
  paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[],
) =>
  !shortlistedCards.length &&
  usesDynamicPaymentProvider(paymentMethodsWithNoAccounts) &&
  paymentMethodsWithNoAccounts.length === 1 &&
  paymentMethodsWithNoAccounts[0];

export const getCpayOnlyMethod = (paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[]) =>
  isUsingCpayPaymentProvider(paymentMethodsWithNoAccounts, undefined) && paymentMethodsWithNoAccounts[0];

export const getSelectedMethodWithAccount = (shortlistedCards: SanitizedPaymentMethod[], selectedCardIndex: number) =>
  selectedCardIndex >= 0 && shortlistedCards[selectedCardIndex];

export const getDepositMethod = (
  savedMethods: SanitizedPaymentMethod[],
  selectedIndex: number,
  paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[],
  selectedMethodWithNoAccounts: PaymentMethodWithNoAccounts | undefined,
) => {
  const selectedMethodWithAccount = getSelectedMethodWithAccount(savedMethods, selectedIndex);
  const cpayOnlyMethod = getCpayOnlyMethod(paymentMethodsWithNoAccounts);
  const singleDynamicPaymentMethod = getSingleDynamicPaymentMethod(savedMethods, paymentMethodsWithNoAccounts);

  const method = selectedMethodWithAccount || selectedMethodWithNoAccounts || cpayOnlyMethod || singleDynamicPaymentMethod || undefined;

  return method;
};

export const getDepositPaymentMethodSource = (
  method: SanitizedPaymentMethod | PaymentMethodWithNoAccounts | undefined,
  paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[],
) => (method ? Number(method.source) : Number(paymentMethodsWithNoAccounts[0].source));

export const getAllowedPaymentMethodsForFirstDeposit = (paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[]) => {
  const userLocation = store.getState().userData.userLocation;

  return paymentMethodsWithNoAccounts.filter(method => {
    const bannedMethodsInUserLocation = blackListedPaymentMethodsForFirstDeposit.find(item => item.country === userLocation);
    return !bannedMethodsInUserLocation || !bannedMethodsInUserLocation.methodIds.includes(method.id);
  });
};

export const canDynamicPaymentMethodBeDetermined = (
  methods: PaymentMethodWithNoAccounts[],
  selectedMethod: PaymentMethodWithNoAccounts | undefined,
) => methods.length === 1 || selectedMethod;

export const getLogoUrlFromMethodWithNoAccounts = (method: PaymentMethodWithNoAccounts) => method.iconSvgUrl || method.iconPngUrl || '';
